import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { ActionDescriptor } from '@finance/contracts';
import { ActionRenderer } from '../experience/ActionRenderer.tsx';
import { resolveActions } from '../../runtime/actions/action-resolution';
import { useEvaluationContext } from '../../runtime/conditions/use-evaluation-context';

export interface SectionHeaderProps {
  id?: string;
  title?: string;
  description?: string;
  actions?: ActionDescriptor[];
  onAction?: (action: ActionDescriptor) => void;
}

export function SectionHeader({ id, title, description, actions, onAction }: SectionHeaderProps) {
  const evalCtx = useEvaluationContext();
  const resolvedActions = useMemo(
    () =>
      resolveActions(actions ?? [], {
        ctx: evalCtx,
      }),
    [actions, evalCtx],
  );

  if (!title && (!actions || actions.length === 0)) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography id={id} variant="h3" component="h2" sx={{ mb: description ? 0.5 : 0 }}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      {resolvedActions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
          {resolvedActions.map((action) => (
            <ActionRenderer key={action.action.id} action={action} onAction={(next) => onAction?.(next.action)} />
          ))}
        </Box>
      )}
    </Box>
  );
}
